import React from "react";
import ImageCropInput from "./ImageCropInput";
import CrossSignControls from "./CrossSignControls";
import SignatureInput from "./SignatureInput";
import { ackFileBaseName } from "src/utils/downloadFileAs";
import {
  AADHAAR_NAME_MAX,
  ACTION_BAR_HEIGHT,
  ATTACHMENT_LAYOUTS,
  AttachmentLayout,
  INCOME_SOURCE_OPTIONS,
  IncomeSource,
  PanFormAttachment,
  PanFormAttachmentPage,
  PanFormValues,
  slotsForLayout,
} from "./panFormFields";
import {
  APPLICANT_STATUS_OPTIONS,
  CodeOption,
  COUNTRY_OPTIONS,
  DocCodeOption,
  POA_OPTIONS,
  PODB_OPTIONS,
  POI_OPTIONS,
  STATE_OPTIONS,
  VERIFIER_CAPACITY_OPTIONS,
} from "./panFormMasters";

// -----------------------------------------------------------------------------
// Step 1 — data entry, laid out like the official PAN entry screen.
//
// The structure and alignment follow that screen (a fieldset per section, a grey
// caption strip, then a strict label/input grid so every column lines up), but
// the palette is deliberately GREYSCALE ONLY — no red, no blue. Mandatory fields
// are marked with a bold black asterisk and, when empty, outlined in black.
//
// Field coverage is driven by the official field spec (69 fields) plus the
// vendor upload layout. Fields the printed form has no box for are still
// captured here — they are stored in `formData` for the vendor file and never
// appear on the PDF (see panFormFields.ts).
//
// NOTE: every input helper below lives at module scope on purpose. Declaring
// them inside the component would give them a new identity on each render, so
// React would remount the <input> after every keystroke and the field would
// lose focus.
// -----------------------------------------------------------------------------

/** Everything the field helpers need, passed down as one prop. */
type Ctx = {
  values: PanFormValues;
  setField: (key: keyof PanFormValues, val: string) => void;
  setFields: (patch: Partial<PanFormValues>) => void;
  invalidKeys: string[];
};

type Props = {
  applicationType: "NEW" | "CORRECTION";
  values: PanFormValues;
  setField: (key: keyof PanFormValues, val: string) => void;
  setFields: (patch: Partial<PanFormValues>) => void;
  toggleBool: (key: keyof PanFormValues) => void;
  toggleIncome: (key: IncomeSource) => void;
  attachments: PanFormAttachment[];
  attachmentPages: PanFormAttachmentPage[];
  onToggleAttachment: (id: string) => void;
  onAttachmentEdited: (id: string, dataUrl: string) => void;
  /** Move an attached document onto another appended sheet, or onto its own. */
  onMoveAttachmentToPage: (id: string, target: string) => void;
  onAddPage: () => void;
  onRemovePage: (pageId: string) => void;
  onSetPageLayout: (pageId: string, layout: AttachmentLayout) => void;
  onSetPageSlot: (pageId: string, slot: number, attachmentId: string) => void;
  /** Field keys that failed the mandatory check on the last "Next" attempt. */
  invalidKeys: string[];
  /** The receipt cut out of the acknowledgement PDF, for the first page. */
  receiptImage?: string | null;
  receiptStatus?: "loading" | "ready" | "none" | "error";
  /** The applicant's own uploaded signature — offered to start over from. */
  originalSignature?: string;
};

const NAME_LEN = 25;

// --- shared cell styling ------------------------------------------------------
const labelCell: React.CSSProperties = {
  background: "#fafafa",
  borderRight: "1px solid #ddd",
  borderBottom: "1px solid #eee",
  padding: "7px 10px",
  fontSize: "12.5px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  minHeight: "38px",
};

const valueCell: React.CSSProperties = {
  padding: "5px 8px",
  borderBottom: "1px solid #eee",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  minHeight: "38px",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid #bbb",
  borderRadius: "2px",
  fontSize: "13px",
  background: "#fff",
  color: "#000",
  boxSizing: "border-box",
};

const invalidStyle: React.CSSProperties = { border: "2px solid #000" };

const checkRow: React.CSSProperties = {
  display: "flex",
  gap: "14px",
  flexWrap: "wrap",
  fontSize: "12.5px",
};

const smallBtn: React.CSSProperties = {
  fontSize: "12px",
  padding: "4px 10px",
  cursor: "pointer",
};

/**
 * Prev / Next inside the Crop / Adjust panel header.
 *
 * Kept in the layout when it cannot be used — greyed rather than removed — so
 * the Close button beside it does not slide sideways as the operator reaches
 * the first or last document and back.
 */
const stepBtn = (enabled: boolean): React.CSSProperties => ({
  ...smallBtn,
  opacity: enabled ? 1 : 0.35,
  cursor: enabled ? "pointer" : "default",
});

/**
 * Every control on the document viewer's dark bar.
 *
 * Defined once, with one height and one border, because the bar previously let
 * each control bring its own box: a bare checkbox label, a native button, a
 * plain underlined link and a 22px glyph, so nothing in the row shared a
 * baseline or a height.
 */
const viewerBtn: React.CSSProperties = {
  height: "28px",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "0 10px",
  fontSize: "12px",
  lineHeight: 1,
  whiteSpace: "nowrap",
  color: "#fff",
  background: "transparent",
  border: "1px solid #6b6b6b",
  borderRadius: "3px",
  cursor: "pointer",
};

/** Prev / Next, one on each side of the document. */
const viewerStepBtn = (enabled: boolean): React.CSSProperties => ({
  flex: "0 0 auto",
  width: "38px",
  height: "64px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px",
  lineHeight: 1,
  color: "#fff",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid #6b6b6b",
  borderRadius: "3px",
  // Kept in the layout when it cannot be used, so the document does not shift
  // sideways as the operator steps to the first or last one.
  opacity: enabled ? 1 : 0.25,
  cursor: enabled ? "pointer" : "default",
});

const bad = (ctx: Ctx, k: keyof PanFormValues) => ctx.invalidKeys.includes(k as string);
const fieldStyle = (ctx: Ctx, k: keyof PanFormValues): React.CSSProperties =>
  bad(ctx, k) ? { ...inputStyle, ...invalidStyle } : inputStyle;

// --- layout primitives --------------------------------------------------------

const Section = ({
  title,
  caption,
  children,
}: {
  title: string;
  caption?: string;
  children: React.ReactNode;
}) => (
  <fieldset style={{ border: "1px solid #ccc", margin: "0 0 14px 0", padding: 0 }}>
    <legend
      style={{
        fontSize: "12.5px",
        fontWeight: 700,
        padding: "0 6px",
        margin: "0 8px",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </legend>
    {caption ? (
      <div
        style={{
          background: "#ececec",
          borderBottom: "1px solid #ddd",
          padding: "7px 10px",
          fontSize: "12.5px",
          fontWeight: 700,
        }}
      >
        {caption}
      </div>
    ) : null}
    <div>{children}</div>
  </fieldset>
);

/** label | input | label | input — the fixed label columns are what align the form. */
const Grid = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "grid", gridTemplateColumns: "230px 1fr 230px 1fr" }}>
    {children}
  </div>
);

const Row = ({
  label,
  required,
  hint,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** Let the input span the remaining columns (email, name triples, tick rows). */
  full?: boolean;
  children: React.ReactNode;
}) => (
  <>
    <div style={labelCell}>
      <span>
        {label}
        {required ? <b> *</b> : null}
        {hint ? <span style={{ fontWeight: 400, color: "#666" }}> {hint}</span> : null}
      </span>
    </div>
    <div style={{ ...valueCell, gridColumn: full ? "2 / -1" : undefined }}>
      {children}
    </div>
  </>
);

/** Filler so a lone field in a 2-per-row grid keeps the following rows aligned. */
const Spacer = () => (
  <>
    <div style={labelCell} />
    <div style={valueCell} />
  </>
);

// --- field helpers ------------------------------------------------------------

const Text = ({
  ctx,
  k,
  maxLength,
  placeholder,
  digitsOnly,
}: {
  ctx: Ctx;
  k: keyof PanFormValues;
  maxLength?: number;
  placeholder?: string;
  /** Drop anything that is not a digit as it is typed (DOB, Aadhaar, PIN…). */
  digitsOnly?: boolean;
}) => (
  <input
    type="text"
    // `data-pan-field` is what the mandatory check uses to scroll the first
    // empty field into view — keep it on every input the operator can fill.
    data-pan-field={k as string}
    value={(ctx.values[k] as string) || ""}
    maxLength={maxLength}
    placeholder={placeholder}
    onChange={(e) =>
      ctx.setField(k, digitsOnly ? e.target.value.replace(/\D/g, "") : e.target.value)
    }
    style={fieldStyle(ctx, k)}
  />
);

/** Text input with a live n/limit counter — the form boxes are char(25). */
const Boxed = ({
  ctx,
  k,
  limit = NAME_LEN,
  digitsOnly,
}: {
  ctx: Ctx;
  k: keyof PanFormValues;
  limit?: number;
  /** Drop anything that is not a digit as it is typed (PIN…). */
  digitsOnly?: boolean;
}) => (
  <>
    <input
      type="text"
      data-pan-field={k as string}
      value={(ctx.values[k] as string) || ""}
      maxLength={limit}
      onChange={(e) =>
        ctx.setField(k, digitsOnly ? e.target.value.replace(/\D/g, "") : e.target.value)
      }
      style={fieldStyle(ctx, k)}
    />
    <span style={{ fontSize: "11px", color: "#777", whiteSpace: "nowrap" }}>
      {((ctx.values[k] as string) || "").length}/{limit}
    </span>
  </>
);

/**
 * Code dropdown that also writes the printed text field. The vendor file wants
 * the master code while the form prints the plain name, so one selection sets
 * both and the PDF template needs no lookup of its own.
 */
const CodeSelect = ({
  ctx,
  codeKey,
  textKey,
  options,
}: {
  ctx: Ctx;
  codeKey: keyof PanFormValues;
  textKey?: keyof PanFormValues;
  options: CodeOption[];
}) => (
  <select
    data-pan-field={codeKey as string}
    value={(ctx.values[codeKey] as string) || ""}
    onChange={(e) => {
      const code = e.target.value;
      const hit = options.find((o) => o.code === code);
      ctx.setFields({
        [codeKey]: code,
        ...(textKey ? { [textKey]: hit ? hit.name : "" } : {}),
      } as Partial<PanFormValues>);
    }}
    style={fieldStyle(ctx, codeKey)}
  >
    <option value="">Select</option>
    {options.map((o, i) => (
      <option key={`${o.code}-${i}`} value={o.code}>
        {o.name} [ {o.code} ]
      </option>
    ))}
  </select>
);

/**
 * Document-code dropdown (POI / POA / PODB). Only ONE document is submitted per
 * proof and the printed form carries nothing but a tick box for it — the name is
 * never printed — so a collapsed list is the right shape: the code goes to the
 * vendor file and the form's own tick box fills itself off the fact that a code
 * was picked at all.
 */
const DocSelect = ({
  ctx,
  k,
  options,
}: {
  ctx: Ctx;
  k: keyof PanFormValues;
  options: DocCodeOption[];
}) => (
  <select
    data-pan-field={k as string}
    value={(ctx.values[k] as string) || ""}
    onChange={(e) => ctx.setField(k, e.target.value)}
    style={fieldStyle(ctx, k)}
  >
    <option value="">Select</option>
    {options.map((o, i) => (
      <option key={`${o.code}-${i}`} value={o.code}>
        {o.label} [ {o.code} ]
      </option>
    ))}
  </select>
);

/**
 * A fixed set of choices collapsed into one dropdown.
 *
 * Gender is captured this way rather than as radios because the vendor's own
 * entry screen presents it as a dropdown — an operator working the two screens
 * side by side reads and fills the same control in both, instead of counting
 * radios here and picking from a list there. The printed form is unaffected:
 * it still ticks one of the three boxes off the stored value.
 */
const Choice = ({
  ctx,
  k,
  options,
  placeholder = "Select",
}: {
  ctx: Ctx;
  k: keyof PanFormValues;
  options: { value: string; label: string }[];
  /**
   * What the unpicked state reads. Defaults to the plain "Select" the coded
   * dropdowns on this form use; Gender asks for the ruled-off version, so that
   * an unanswered mandatory choice is unmistakable at a glance rather than
   * looking like a value someone chose.
   */
  placeholder?: string;
}) => (
  <select
    data-pan-field={k as string}
    value={(ctx.values[k] as string) || ""}
    onChange={(e) => ctx.setField(k, e.target.value)}
    style={fieldStyle(ctx, k)}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const Radios = ({
  ctx,
  k,
  name,
  options,
}: {
  ctx: Ctx;
  k: keyof PanFormValues;
  name: string;
  options: { value: string; label: string }[];
}) => (
  <div
    data-pan-field={k as string}
    tabIndex={-1}
    style={{
      ...checkRow,
      // The outline is how an unanswered mandatory choice is flagged — the form
      // uses no colour at all.
      ...(bad(ctx, k) ? { outline: "2px solid #000", padding: "2px 4px" } : {}),
    }}
  >
    {options.map((o) => (
      <label key={o.value} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <input
          type="radio"
          name={name}
          checked={(ctx.values[k] as string) === o.value}
          onChange={() => ctx.setField(k, o.value)}
        />
        {o.label}
      </label>
    ))}
  </div>
);

const NameTriple = ({
  ctx,
  first,
  middle,
  last,
}: {
  ctx: Ctx;
  first: keyof PanFormValues;
  middle: keyof PanFormValues;
  last: keyof PanFormValues;
}) => (
  <div style={{ display: "flex", gap: "8px", width: "100%" }}>
    {([first, middle, last] as const).map((k, i) => (
      <input
        key={k as string}
        data-pan-field={k as string}
        type="text"
        value={(ctx.values[k] as string) || ""}
        maxLength={NAME_LEN}
        placeholder={["First", "Middle", "Last"][i]}
        onChange={(e) => ctx.setField(k, e.target.value)}
        style={fieldStyle(ctx, k)}
      />
    ))}
  </div>
);

/**
 * Address block, reused for residence / office / representative assessee.
 *
 * The row captions are the ones printed on Form 93 / the correction form; where
 * the vendor spec names the same box differently, that name is shown as a hint
 * so the operator always knows which value belongs in the row.
 */
const AddressRows = ({
  ctx,
  prefix,
  required,
}: {
  ctx: Ctx;
  prefix: "" | "office" | "ra";
  required?: boolean;
}) => {
  const k = (base: string) =>
    (prefix
      ? `${prefix}${base.charAt(0).toUpperCase()}${base.slice(1)}`
      : base) as keyof PanFormValues;
  const stateCodeKey: keyof PanFormValues =
    prefix === "" ? "stateCode" : prefix === "office" ? "officeStateCode" : "raStateCode";
  const countryCodeKey: keyof PanFormValues =
    prefix === ""
      ? "countryCode"
      : prefix === "office"
      ? "officeCountryCode"
      : "raCountryCode";

  return (
    <>
      <Row label="Flat / Door / Building" required={required}>
        <Boxed ctx={ctx} k={k("flat")} />
      </Row>
      <Row
        label="Road / Street / Block / Sector"
        required={required}
        hint="(Name of Premises / Building / Village)"
      >
        <Boxed ctx={ctx} k={k("road")} />
      </Row>
      <Row label="Post Office" hint="(Road / Street / Lane / Post Office)">
        <Boxed ctx={ctx} k={k("postOffice")} />
      </Row>
      {/* Lines 3 and 4 are optional: the form only needs the first two address
          lines, plus District / State / PIN, to be filled. */}
      <Row label="Area / Locality / Town / City" hint="(Area / Locality / Taluka)">
        <Boxed ctx={ctx} k={k("area")} />
      </Row>
      <Row label="District" required={required} hint="(Town / City / District)">
        <Boxed ctx={ctx} k={k("district")} />
      </Row>
      <Row label="State / Union Territory" required={required}>
        <CodeSelect
          ctx={ctx}
          codeKey={stateCodeKey}
          textKey={k("state")}
          options={STATE_OPTIONS}
        />
      </Row>
      <Row label="Country / Region">
        <CodeSelect
          ctx={ctx}
          codeKey={countryCodeKey}
          textKey={k("country")}
          options={COUNTRY_OPTIONS}
        />
      </Row>
      {/* An Indian PIN is exactly six digits — the printed grid has six boxes
          and the vendor file rejects anything shorter, so the check on leaving
          step 1 demands all six (see PIN_LEN in PanFormDialog). */}
      <Row label="PIN / ZIP Code" required={required}>
        <Boxed ctx={ctx} k={k("pincode")} limit={6} digitsOnly />
      </Row>
    </>
  );
};

const YES_NO = [
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
];

/**
 * The Crop / Adjust panel on the right of the document viewer.
 *
 * `ImageCropInput` does the work; this decides what the viewer does next, and
 * the two endings are deliberately different:
 *
 * - **Crop & Use / Use full image** — the operator is done with THIS document
 *   and the panel moves on to the next one, still open. A counter hands over a
 *   pile of six or seven scans and every one of them wants straightening, so
 *   closing the panel after each meant re-opening Crop / Adjust six times.
 * - **Cancel** — nothing was changed, so the panel closes and hands the
 *   operator back to the document they were reading.
 *
 * The refs are why this is a component rather than a few props inline.
 * `opened` : `autoEdit` opens the workspace from an effect, so the FIRST
 * editing report of the mount is the widget saying "not open yet", and acting
 * on that would shut the panel on the frame it appeared. It is also cleared as
 * soon as an ending is reported, so a re-render on the way out cannot report
 * the same ending twice — a second `onCommitted` would skip a document.
 * `committed` : `onChange` only ever fires on a commit, so it is what separates
 * the two endings above.
 *
 * Note what it does NOT do: attach the document. Cropping is how the operator
 * reads a bad scan as often as it is how they prepare one for print.
 */
const AttachmentCropPanel = ({
  attachment,
  onEdited,
  onCommitted,
  onCancel,
}: {
  attachment: PanFormAttachment;
  onEdited: (id: string, dataUrl: string) => void;
  onCommitted: () => void;
  onCancel: () => void;
}) => {
  const opened = React.useRef(false);
  const committed = React.useRef(false);
  const onEditingChange = React.useCallback(
    (editing: boolean) => {
      if (editing) {
        opened.current = true;
        committed.current = false;
        return;
      }
      if (!opened.current) return;
      opened.current = false;
      if (committed.current) onCommitted();
      else onCancel();
    },
    [onCommitted, onCancel]
  );

  return (
    <ImageCropInput
      label=""
      itemName="document"
      // A document fills a whole A4 page, so the cap sets its print resolution:
      // 200 DPI across 210 mm needs 1654 px. 2000 keeps a full-page scan
      // comfortably above 200 DPI without bloating the posted HTML.
      maxSize={2000}
      previewMaxHeight={160}
      previewMaxWidth={320}
      // The panel only exists because the operator asked to crop, so it opens
      // straight into the workspace rather than on another preview.
      autoEdit
      grayscaleByDefault
      value={attachment.edited || attachment.url}
      onChange={(dataUrl) => {
        committed.current = true;
        onEdited(attachment.id, dataUrl);
      }}
      onEditingChange={onEditingChange}
    />
  );
};

// -----------------------------------------------------------------------------

const PanFormStepOne = ({
  applicationType,
  values,
  setField,
  setFields,
  toggleBool,
  toggleIncome,
  attachments,
  attachmentPages,
  onToggleAttachment,
  onAttachmentEdited,
  onMoveAttachmentToPage,
  onAddPage,
  onRemovePage,
  onSetPageLayout,
  onSetPageSlot,
  invalidKeys,
  receiptImage = null,
  receiptStatus = "none",
  originalSignature = "",
}: Props) => {
  const isNew = applicationType !== "CORRECTION";
  /*
    The document opened full-size, held as an id only — everything else is
    re-read from `attachments`. That is what lets Attach ticked from inside the
    viewer show its new state, a crop committed under it replace the image, and
    Prev / Next be one step along the same array the cards are drawn from.

    Clicking a thumbnail used to toggle its tick, which meant the operator could
    not enlarge a scan to read it without also attaching it. The thumbnail opens
    it; the tick box alone attaches it.
  */
  const [viewerId, setViewerId] = React.useState<string | null>(null);
  /*
    Crop / Adjust opens as a panel down the RIGHT of the viewer.

    It used to expand the card in place, which meant the card had to take the
    whole grid row to fit the sliders — so the grid reflowed under the operator,
    and with the viewer open on top of it the two covered each other (the crop
    row's "Close" ended up floating in the middle of the screen). In the viewer
    the document stays put, the panel owns the controls, and closing the panel
    hands the operator back to the document with Prev / Next still under their
    hand.
  */
  const [cropOpen, setCropOpen] = React.useState(false);
  /*
    The photo and signature strip is pinned to the top of the form so the
    operator can see both while filling any part of it. It un-pins while either
    crop workspace is open: the workspace is several hundred pixels tall and
    pinned it would cover the form instead of sitting above it.
  */
  const [photoEditing, setPhotoEditing] = React.useState(false);
  const [signatureEditing, setSignatureEditing] = React.useState(false);
  const stripEditing = photoEditing || signatureEditing;

  const viewerIndex = viewerId ? attachments.findIndex((a) => a.id === viewerId) : -1;
  const viewed = viewerIndex >= 0 ? attachments[viewerIndex] : null;
  const hasPrev = viewerIndex > 0;
  const hasNext = viewerIndex >= 0 && viewerIndex < attachments.length - 1;

  const openViewer = (id: string, crop = false) => {
    setViewerId(id);
    setCropOpen(crop);
  };
  const closeViewer = () => {
    setViewerId(null);
    setCropOpen(false);
  };
  /*
    Step to the document before or after this one, in card order.

    The crop panel STAYS open across the step. It used to close, because its
    workspace holds an uncommitted crop and carrying that across would apply
    one document's box to a different image — but the panel is keyed on the
    document id, so stepping remounts it and the next scan opens in a workspace
    of its own. Keeping it open is what lets a pile of scans be cropped in one
    pass instead of one Crop / Adjust click per document.
  */
  const stepViewer = React.useCallback(
    (delta: number) => {
      if (viewerIndex < 0) return;
      const next = attachments[viewerIndex + delta];
      if (!next) return;
      setViewerId(next.id);
    },
    [attachments, viewerIndex]
  );

  /*
    Crop & Use / Use full image, with more documents behind this one: move
    straight on to the next, panel still open, so the operator works down the
    pile without going back to the card grid between each.

    On the LAST document there is nothing to move to, so the panel closes —
    that is the operator's "done", and leaving it open on an image they have
    already committed only invites a second crop of the same scan.
  */
  const cropCommitted = React.useCallback(() => {
    const next = attachments[viewerIndex + 1];
    if (next) setViewerId(next.id);
    else setCropOpen(false);
  }, [attachments, viewerIndex]);

  /*
    Keyboard: Esc out, arrows along.

    Esc closes the crop panel FIRST when it is open — it is the innermost thing
    on screen, and one Esc throwing away both the panel and the document the
    operator was reading is a key nobody presses twice. The arrows are ignored
    while the panel is open, for the same reason `stepViewer` closes it.
  */
  React.useEffect(() => {
    if (!viewerId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (cropOpen) setCropOpen(false);
        else closeViewer();
        return;
      }
      if (cropOpen) return;
      if (e.key === "ArrowLeft") stepViewer(-1);
      if (e.key === "ArrowRight") stepViewer(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerId, cropOpen, stepViewer]);

  const ctx: Ctx = { values, setField, setFields, invalidKeys };
  const selectable = attachments.filter((a) => a.selected);
  /*
    Which appended sheet a document landed on.

    Ticking Attach builds a page for the document silently, at the far end of a
    long form, so the operator had no way of knowing what ticking had just done
    — least of all from inside the viewer, where the page builder is off screen
    entirely. The card and the viewer both show the answer now.
  */
  const pageIndexOf = (id: string) =>
    attachmentPages.findIndex((p) => p.items.includes(id));

  /*
    What one slot's dropdown offers.

    Every slot used to list `selectable` whole, so picking a document that was
    already on an earlier sheet put the same scan on two appended pages with
    nothing on screen to say so. That was then fixed by dropping placed
    documents out of the other dropdowns — and with two documents attached and
    both placed, every remaining slot offered nothing but `— empty —`, which is
    the empty list that was reported next.

    So a placed document is LISTED again, marked with the page it is sitting on.
    Picking it moves it there: `setPageSlot` clears a document out of every
    other slot before filling this one, so one document still occupies exactly
    one slot however it is chosen, and the same scan can never print twice.
    The operator gets a dropdown that always has something in it, and the label
    says what choosing it will do.

    `— empty —` is rendered by the caller and always available, so a slot can
    still be cleared.
  */
  const optionsForSlot = (pageId: string, slot: number) => {
    const mine = attachmentPages.find((p) => p.id === pageId)?.items[slot] || "";
    return selectable.map((a) => {
      const at = a.id === mine ? -1 : pageIndexOf(a.id);
      return {
        id: a.id,
        label: at < 0 ? a.label : `${a.label}  \u2014  move from page ${at + 1}`,
      };
    });
  };

  return (
    /*
      No scroll box of its own, and no side gutter of its own.

      Both are left over from when this form was a dialog: a `maxHeight: 68vh`
      with `overflowY: auto` gave the form a second scrollbar inside the page's
      own, so the operator scrolled a tall government form through a short
      window while the page's footer sat below it — and the 16px of side
      padding put the form on a fourth left edge, inside the page's gutter. On a
      page of its own the form simply flows, and the page scrolls.
    */
    <div
      style={{
        padding: "0 0 14px",
        background: "#fff",
        color: "#000",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* ------------------------------------------- photo + signature --- */}
      {/* One row, both halves side by side, pinned directly below the form's
          action bar. They are the two things the operator checks against every
          other field on the form, so they stay on screen instead of scrolling
          away at the top of the personal-detail section.

          `top` clears the bar rather than the page (ACTION_BAR_HEIGHT) — pinned
          at 0 the strip would sit behind it, which is where it went when the
          bar moved from the foot of the form to the head of it. */}
      <div
        style={{
          position: stripEditing ? "static" : "sticky",
          top: ACTION_BAR_HEIGHT,
          zIndex: 3,
          background: "#fff",
          paddingTop: "14px",
          marginBottom: "12px",
          borderBottom: "1px solid #ccc",
        }}
      >
        {/* Side by side normally. The crop workspace is ~460px wide, so while
            one is open the strip drops to a single full-width column — the two
            halves stack for as long as the crop is on screen and pair up again
            the moment it closes. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: stripEditing ? "230px 1fr" : "230px 1fr 230px 1fr",
          }}
        >
          <div style={{ ...labelCell, borderTop: "1px solid #ccc" }}>
            <span>Photo</span>
          </div>
          <div style={{ ...valueCell, borderTop: "1px solid #ccc", minWidth: 0 }}>
            <ImageCropInput
              label=""
              itemName="photograph"
              // No locked ratio: the crop is free here as it is everywhere else
              // in the admin. The printed photo box scales whatever it is given,
              // and a locked box cost the operator the mid-edge handles.
              previewMaxHeight={110}
              previewMaxWidth={86}
              value={values.photo}
              onChange={(dataUrl) => setField("photo", dataUrl)}
              onEditingChange={setPhotoEditing}
              resizePreset="photo"
              resizeFileName={ackFileBaseName(values.acknowledgementNumber, "Photo", "photo")}
            />
          </div>
          <div style={{ ...labelCell, borderTop: "1px solid #ccc" }}>
            <span>Signature</span>
          </div>
          <div style={{ ...valueCell, borderTop: "1px solid #ccc", minWidth: 0 }}>
            <SignatureInput
              value={values.signature}
              onChange={(dataUrl) => setField("signature", dataUrl)}
              onEditingChange={setSignatureEditing}
              originalUrl={originalSignature}
              resizeFileName={ackFileBaseName(values.acknowledgementNumber, "Sig", "signature")}
            />
          </div>
        </div>

        {/* What prints besides the form fields: the signature across the left
            photo, and the acknowledgement receipt as page 1. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px 24px",
            padding: "8px 0",
            fontSize: "13px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={values.crossSign === "YES"}
              onChange={(e) => setField("crossSign", e.target.checked ? "YES" : "NO")}
            />
            <span>Cross sign on left photo</span>
            {values.crossSign === "YES" && !values.signature && (
              <span style={{ color: "#c62828" }}>(add a signature first)</span>
            )}
          </label>
          {values.crossSign === "YES" && values.signature && (
            <CrossSignControls values={values} setFields={setFields} />
          )}

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: receiptImage ? "pointer" : "not-allowed",
              opacity: receiptImage ? 1 : 0.6,
            }}
          >
            <input
              type="checkbox"
              disabled={!receiptImage}
              checked={!!receiptImage && values.receiptPage === "YES"}
              onChange={(e) => setField("receiptPage", e.target.checked ? "YES" : "NO")}
            />
            <span>Add receipt as first page</span>
            {receiptStatus === "loading" && (
              <span style={{ color: "#666" }}>(reading acknowledgement PDF…)</span>
            )}
            {receiptStatus === "none" && (
              <span style={{ color: "#666" }}>(Acknowledgement PDF not uploaded)</span>
            )}
            {receiptStatus === "error" && (
              <span style={{ color: "#c62828" }}>(could not read acknowledgement PDF)</span>
            )}
          </label>

          {receiptImage && values.receiptPage === "YES" && (
            <img
              src={receiptImage}
              alt="Receipt preview"
              style={{ height: "56px", border: "1px solid #ccc" }}
            />
          )}
        </div>
      </div>

      {invalidKeys.length > 0 && (
        <div
          style={{
            border: "2px solid #000",
            padding: "8px 10px",
            marginBottom: "12px",
            fontSize: "12.5px",
            fontWeight: 700,
          }}
        >
          {invalidKeys.length} mandatory field
          {invalidKeys.length > 1 ? "s are" : " is"} still empty or incomplete —
          outlined below.
        </div>
      )}

      {/* ------------------------------------------------ personal detail --- */}
      <Section
        title="APPLICANT PERSONAL DETAIL"
        caption="Fill Applicants Personal Detail Here"
      >
        <Grid>
          <Row label="Applicant Status" required>
            <CodeSelect ctx={ctx} codeKey="applicantStatus" options={APPLICANT_STATUS_OPTIONS} />
          </Row>
          <Row label="Acknowledge Number">
            <Text ctx={ctx} k="acknowledgementNumber" maxLength={20} />
          </Row>

          <Row label="Country of Citizenship" required>
            <CodeSelect ctx={ctx} codeKey="countryOfCitizenship" options={COUNTRY_OPTIONS} />
          </Row>
          <Row label="Registration Number" hint="(non-individual)">
            <Text ctx={ctx} k="registrationNo" maxLength={25} />
          </Row>

          <Row label="Full Name (First, Middle, Last)" required full>
            <NameTriple ctx={ctx} first="firstName" middle="middleName" last="lastName" />
          </Row>

          {/* The printed block is three rows of 25 boxes and the value is
              word-wrapped across them, so the input takes the whole 75. */}
          <Row label="Name as per Aadhaar" required hint="(3 rows of 25)" full>
            <Boxed ctx={ctx} k="aadhaarName" limit={AADHAAR_NAME_MAX} />
          </Row>

          <Row label="Gender" required>
            <Choice
              ctx={ctx}
              k="gender"
              placeholder="------ SELECT ------"
              options={[
                { value: "MALE", label: "Male" },
                { value: "FEMALE", label: "Female" },
                { value: "TRANSGENDER", label: "Transgender" },
              ]}
            />
          </Row>
          <Row label="Date of Birth" required hint="(DDMMYYYY)">
            {/* Digits only: the PDF prints one character per box, so a typed
                "01/01/2001" would fill the boxes with slashes and lose the
                year to `maxLength`. */}
            <Text ctx={ctx} k="dob" maxLength={8} placeholder="DDMMYYYY" digitsOnly />
          </Row>

          <Row label="Aadhaar Number">
            <Text ctx={ctx} k="aadhaar" maxLength={12} digitsOnly />
          </Row>
          <Row label="Aadhaar Enrolment ID (EID)" hint="(if no Aadhaar)">
            <Text ctx={ctx} k="aadhaarEid" maxLength={28} />
          </Row>

          <Row label="Known by any other name?">
            <Radios ctx={ctx} k="hasOtherName" name="hasOtherName" options={YES_NO} />
          </Row>
          <Row label="Physical PAN card required?">
            <Radios ctx={ctx} k="physicalPanCard" name="physicalPanCard" options={YES_NO} />
          </Row>

          {values.hasOtherName === "YES" && (
            <Row label="Other Name (First, Middle, Last)" full>
              <NameTriple
                ctx={ctx}
                first="otherFirstName"
                middle="otherMiddleName"
                last="otherLastName"
              />
            </Row>
          )}

          {!isNew ? (
            <>
              <Row label="PAN Number" required>
                <Text ctx={ctx} k="pan" maxLength={10} />
              </Row>
              <Spacer />
            </>
          ) : (
            <>
              <Row label="Passport Number" hint="(non-resident)">
                <Text ctx={ctx} k="passport" maxLength={13} />
              </Row>
              <Row label="TIN (Country of Residence)">
                <Text ctx={ctx} k="tin" maxLength={20} />
              </Row>
              <Row label="Residential Status" required full>
                <Radios
                  ctx={ctx}
                  k="residentialStatus"
                  name="residentialStatus"
                  options={[
                    { value: "RESIDENT", label: "Resident" },
                    { value: "NON_RESIDENT", label: "Non Resident" },
                    {
                      value: "NOT_ORDINARY",
                      label: "Resident but Not ordinarily Resident",
                    },
                  ]}
                />
              </Row>
            </>
          )}

          <Row label="Father Name (First, Middle, Last)" required full>
            <NameTriple ctx={ctx} first="fatherFirst" middle="fatherMiddle" last="fatherLast" />
          </Row>
          <Row label="Mother Name (First, Middle, Last)" required full>
            <NameTriple ctx={ctx} first="motherFirst" middle="motherMiddle" last="motherLast" />
          </Row>

          <Row label="Single parent?" hint="(mother / father)">
            <Radios ctx={ctx} k="singleParent" name="singleParent" options={YES_NO} />
          </Row>
          <Row label="Name printed on PAN card" required>
            <Radios
              ctx={ctx}
              k="printParent"
              name="printParent"
              options={[
                { value: "FATHER", label: "Father" },
                { value: "MOTHER", label: "Mother" },
              ]}
            />
          </Row>
        </Grid>
      </Section>

      {/* ------------------------------------------------------- addresses --- */}
      {/*
        The correction form has ONE address block, and its Residence / Office
        tick says what kind of address is in it — so the heading follows the
        tick rather than always claiming "Residential". (The NEW form is
        different: it has a separate office block further down, gated on the
        address for communication.)
      */}
      <Section
        title={
          !isNew && values.addressType === "OFFICE"
            ? "APPLICANTS OFFICE ADDRESS DETAIL"
            : "APPLICANTS RESIDENTIAL ADDRESS DETAIL"
        }
        caption={
          !isNew && values.addressType === "OFFICE"
            ? "Fill Office Address Detail Here"
            : "Fill Residential Address Detail Here"
        }
      >
        <Grid>
          {!isNew && (
            <Row label="Address type" required full>
              <Radios
                ctx={ctx}
                k="addressType"
                name="addressType"
                options={[
                  { value: "RESIDENCE", label: "Residence" },
                  { value: "OFFICE", label: "Office" },
                ]}
              />
            </Row>
          )}
          <AddressRows ctx={ctx} prefix="" required />
        </Grid>
      </Section>

      {/*
        Office and RA addresses are only asked for when they are the address the
        department writes to — that is what "Address for Communication" picks.
        Left visible they read as two more blocks to fill on every application;
        gated, they appear only for the applicant who actually needs them, and
        are mandatory once chosen. Switching the choice clears the block that is
        no longer in play (see setField in PanFormDialog), so nothing typed and
        then hidden can still reach the PDF.
      */}
      {isNew && values.addressForComm === "OFFICE" && (
        <Section
          title="APPLICANTS OFFICE ADDRESS DETAIL"
          caption="Fill Office Address Detail Here (this is the address for communication)"
        >
          <Grid>
            <AddressRows ctx={ctx} prefix="office" required />
          </Grid>
        </Section>
      )}

      {/* --------------------------------------------------------- contact --- */}
      <Section title="APPLICANTS CONTACT DETAIL" caption="Fill Contact Detail Here">
        <Grid>
          <Row label="Country Code (Mobile)" required>
            <Text ctx={ctx} k="mobileCountryCode" maxLength={7} />
          </Row>
          <Row label="Mobile Number" required>
            <Text ctx={ctx} k="mobile" maxLength={13} digitsOnly />
          </Row>
          <Row label="Email ID" required full>
            <Text ctx={ctx} k="email" maxLength={40} />
          </Row>
          {isNew && (
            <>
              <Row label="STD Code">
                <Text ctx={ctx} k="landlineStd" maxLength={7} />
              </Row>
              <Row label="Landline Number">
                <Text ctx={ctx} k="landline" maxLength={13} />
              </Row>
              <Row label="Address for Communication" required full>
                <Radios
                  ctx={ctx}
                  k="addressForComm"
                  name="addressForComm"
                  options={[
                    { value: "RESIDENCE", label: "Residence Address" },
                    { value: "RA", label: "Representative Assessee Address" },
                    { value: "OFFICE", label: "Office Address" },
                  ]}
                />
              </Row>
            </>
          )}
        </Grid>
      </Section>

      {/* ---------------------------------------------------------- income --- */}
      {/*
        NEW (Form 49A / Form No. 93) only. The correction form has no source-of-income
        item — CORRECTION_FORM_FIELDS carries no tick for it — so asking the operator
        for it there is a mandatory field that prints nowhere.
      */}
      {isNew && (
      <Section
        title="APPLICANTS INCOME SOURCE DETAIL"
        caption="Fill Income Source Detail Here"
      >
        <Grid>
          <Row label="Income Source Type" required full>
            <div
              data-pan-field="incomeSources"
              tabIndex={-1}
              style={{
                ...checkRow,
                ...(invalidKeys.includes("incomeSources")
                  ? { outline: "2px solid #000", padding: "2px 4px" }
                  : {}),
              }}
            >
              {INCOME_SOURCE_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <input
                    type="checkbox"
                    checked={values.incomeSources.includes(opt.key)}
                    onChange={() => toggleIncome(opt.key)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </Row>
        </Grid>
      </Section>
      )}

      {/* ------------------------------------------ representative assessee --- */}
      {isNew && values.addressForComm === "RA" && (
        <Section
          title="REPRESENTATIVE ASSESSEE DETAIL"
          caption="Fill Representative Assessee Detail Here (this is the address for communication)"
        >
          <Grid>
            <Row label="RA Name (First, Middle, Last)" required full>
              <NameTriple ctx={ctx} first="raFirst" middle="raMiddle" last="raLast" />
            </Row>
            <Row label="RA PAN">
              <Text ctx={ctx} k="raPan" maxLength={10} />
            </Row>
            <Row label="RA Aadhaar Number" required>
              <Text ctx={ctx} k="raAadhaar" maxLength={12} />
            </Row>
            <AddressRows ctx={ctx} prefix="ra" required />
            <Row label="RA Country Code (Mobile)">
              <Text ctx={ctx} k="raMobileCC" maxLength={7} />
            </Row>
            <Row label="RA Mobile Number" required>
              <Text ctx={ctx} k="raMobile" maxLength={13} />
            </Row>
            <Row label="RA Email ID" required full>
              <Text ctx={ctx} k="raEmail" maxLength={40} />
            </Row>
            <Row label="RA STD Code">
              <Text ctx={ctx} k="raLandlineStd" maxLength={7} />
            </Row>
            <Row label="RA Landline Number">
              <Text ctx={ctx} k="raLandline" maxLength={13} />
            </Row>
          </Grid>
        </Section>
      )}

      {/* --------------------------------------------------- assessing officer */}
      {/* Part D of Form 49A / Form No. 93 only. The correction form carries no
          AO Code box, so a correction application is never asked for one. */}
      {isNew && (
        <Section
          title="ASSESSING OFFICER (AO) CODE"
          caption="Fill the four AO Code cells printed in Part D of the form"
        >
          <Grid>
            {/* Exactly the four cells the form prints, and each maxLength is
                that cell count (Area 3, AO Type 2, Range 3, AO No 2) so nothing
                typed here is dropped when the PDF is generated. */}
            <Row label="Area Code" required>
              <Text ctx={ctx} k="aoAreaCode" maxLength={3} />
            </Row>
            <Row label="AO Type" required>
              <Text ctx={ctx} k="aoType" maxLength={2} />
            </Row>
            <Row label="Range Code" required>
              <Text ctx={ctx} k="aoRangeCode" maxLength={3} />
            </Row>
            <Row label="AO Number" required>
              <Text ctx={ctx} k="aoNo" maxLength={2} />
            </Row>
          </Grid>
        </Section>
      )}

      {/* ------------------------------------------------- documents / proof */}
      <Section
        title="DOCUMENTS / PROOF DETAIL"
        caption="Pick the document submitted for each proof — the code goes to the vendor file and the form's own tick box is filled automatically"
      >
        <Grid>
          <Row label="Proof of Identity Type" required full>
            <DocSelect ctx={ctx} k="poiCode" options={POI_OPTIONS} />
          </Row>
          <Row label="Proof of Address Type" required full>
            <DocSelect ctx={ctx} k="poaCode" options={POA_OPTIONS} />
          </Row>
          <Row label="Proof of DOB Type" required full>
            <DocSelect ctx={ctx} k="podbCode" options={PODB_OPTIONS} />
          </Row>
          {/*
            The RA's own proofs are only asked for alongside the RA block itself,
            i.e. when the RA address was picked as the address for communication.
          */}
          {isNew && values.addressForComm === "RA" && (
            <>
              <Row label="RA Proof of Identity Type" required full>
                <DocSelect ctx={ctx} k="raPoiCode" options={POI_OPTIONS} />
              </Row>
              <Row label="RA Proof of Address Type" required full>
                <DocSelect ctx={ctx} k="raPoaCode" options={POA_OPTIONS} />
              </Row>
            </>
          )}
          {!isNew && (
            <Row label="Other proof ticks" full>
              <div style={checkRow}>
                {(
                  [
                    { key: "corrDocProof", label: "Documentary proof (other changes)" },
                    { key: "corrCopyOfPan", label: "Copy of PAN" },
                  ] as const
                ).map((o) => (
                  <label
                    key={o.key}
                    style={{ display: "flex", alignItems: "center", gap: "5px" }}
                  >
                    <input
                      type="checkbox"
                      checked={!!values[o.key]}
                      onChange={() => toggleBool(o.key)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </Row>
          )}
        </Grid>
      </Section>

      {/* ---------------------------------------- correction change tick box */}
      {!isNew && (
        <Section
          title="FIELDS BEING CHANGED / CORRECTED"
          caption="Tick Box column (Sr. No. 1-9) on the correction form"
        >
          <Grid>
            <Row label="Change requested for" full>
              <div style={checkRow}>
                {(
                  [
                    { key: "changeName", label: "1. Name" },
                    { key: "changeGender", label: "2. Gender" },
                    { key: "changeDob", label: "3. Date of Birth" },
                    { key: "changeAddress", label: "4. Address" },
                    { key: "changePassport", label: "5. Passport Number" },
                    { key: "changeTin", label: "6. TIN" },
                    { key: "changeContact", label: "7. Contact Details" },
                    { key: "changeFather", label: "8. Father's Name" },
                    { key: "changeMother", label: "9. Mother's Name" },
                  ] as const
                ).map((o) => (
                  <label
                    key={o.key}
                    style={{ display: "flex", alignItems: "center", gap: "5px" }}
                  >
                    <input
                      type="checkbox"
                      checked={!!values[o.key]}
                      onChange={() => toggleBool(o.key)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </Row>
          </Grid>
        </Section>
      )}

      {/* --------------------------------------------------- other details --- */}
      <Section title="OTHER DETAILS" caption="Fill Other Details">
        <Grid>
          <Row label="Date of Receipt" required>
            <Text ctx={ctx} k="dateOfReceipt" maxLength={10} placeholder="dd/mm/yyyy" />
          </Row>
          <Row label="Date of Verification" required>
            <Text ctx={ctx} k="dateOfVerification" maxLength={10} placeholder="dd/mm/yyyy" />
          </Row>
          <Row label="Name of Verifier" required>
            <Text ctx={ctx} k="declarantName" maxLength={75} />
          </Row>
          <Row label="Capacity of Verifier" required>
            <CodeSelect
              ctx={ctx}
              codeKey="verifierCapacityCode"
              textKey="declarantCapacity"
              options={VERIFIER_CAPACITY_OPTIONS}
            />
          </Row>
          <Row label="Place of Verifier" required>
            <Text ctx={ctx} k="place" maxLength={30} />
          </Row>
          <Row label="Declaration Date" required hint="(printed on the form)">
            <Text ctx={ctx} k="date" maxLength={10} placeholder="dd/mm/yyyy" />
          </Row>
          <Row label="Photo Present" required>
            <Radios ctx={ctx} k="photoPresent" name="photoPresent" options={YES_NO} />
          </Row>
          <Row label="Signature Present" required>
            <Radios ctx={ctx} k="signaturePresent" name="signaturePresent" options={YES_NO} />
          </Row>
          {isNew && (
            <>
              <Row label="Designation">
                <Text ctx={ctx} k="designation" maxLength={30} />
              </Row>
              <Spacer />
            </>
          )}
        </Grid>
      </Section>

      {/* --------------------------------------------- document attachments --- */}
      <Section
        title="ATTACH UPLOADED DOCUMENTS"
        caption="Click a document to view it full size (\u2190 \u2192 steps through them); tick Attach to append it, then arrange the appended A4 pages below"
      >
        <div style={{ padding: "10px" }}>
          {attachments.length === 0 ? (
            <div style={{ fontSize: "12.5px", color: "#666" }}>
              No uploaded documents on this application.
            </div>
          ) : (
            <>
              {/*
                A card per document rather than a row: the thumbnails in the old
                list were 40px squares, which is too small to tell one scan of a
                PAN form from another, and the operator picks these by eye.

                The tick-box and Crop / Adjust sit under each preview and stay
                invisible until the card is hovered or something inside it takes
                keyboard focus, so a wall of cards reads as documents rather than
                as controls. They keep their space either way, so nothing shifts
                as the pointer moves. A card that is already ticked keeps them on
                screen — a selection the operator cannot see without hunting for
                it with the mouse is worse than a little extra ink.
              */}
              <style>{`
                .pf-doc-grid {
                  display: grid;
                  /* Three to a row. minmax(0,1fr) rather than 1fr so a wide
                     scan cannot push its column past its share. */
                  grid-template-columns: repeat(3, minmax(0, 1fr));
                  gap: 10px;
                }
                .pf-doc-card {
                  border: 1px solid #ddd;
                  background: #fff;
                  padding: 8px;
                }
                .pf-doc-card:hover,
                .pf-doc-card:focus-within { border-color: #888; }
                .pf-doc-card.is-selected { border-color: #000; box-shadow: inset 0 0 0 1px #000; }
                /* Always on screen. They used to fade in on hover, which was
                   fine while the thumbnail itself also toggled the tick — now
                   the tick box is the ONLY way to attach a document, so hiding
                   it until the pointer arrives would hide the control that does
                   the work. */
                .pf-doc-tick {
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  font-size: 12px;
                  font-weight: 600;
                  cursor: pointer;
                }
              `}</style>

              <div className="pf-doc-grid">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    id={`pf-doc-card-${a.id}`}
                    className={`pf-doc-card${a.selected ? " is-selected" : ""}`}
                  >
                    <div
                      style={{
                        fontSize: "12.5px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={a.label}
                    >
                      {a.label}
                      {a.edited && (
                        <span style={{ color: "#666", fontSize: "11px", fontWeight: 400 }}>
                          {" "}
                          (adjusted for print)
                        </span>
                      )}
                    </div>

                    {/* The preview opens the document full-size. Attaching is
                        the tick box's job and only the tick box's job — a card
                        thumbnail is too small to read a scan from, and the
                        operator has to be able to check one without that click
                        changing what goes into the PDF. */}
                    <div
                      onClick={() => openViewer(a.id)}
                      title="Click to view full size"
                      style={{
                        height: "220px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#fafafa",
                        border: "1px solid #eee",
                        cursor: "zoom-in",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={a.edited || a.url}
                        alt={a.label}
                        crossOrigin="anonymous"
                        style={{
                          maxHeight: "100%",
                          maxWidth: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>

                    <div
                      className="pf-doc-actions"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginTop: "6px",
                      }}
                    >
                      <label className="pf-doc-tick">
                        <input
                          type="checkbox"
                          checked={a.selected}
                          onChange={() => onToggleAttachment(a.id)}
                        />
                        Attach
                        {a.selected && pageIndexOf(a.id) >= 0 && (
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: "11px",
                              color: "#444",
                              border: "1px solid #bbb",
                              borderRadius: "2px",
                              padding: "0 4px",
                            }}
                            title="The appended A4 sheet this document prints on"
                          >
                            Page {pageIndexOf(a.id) + 1}
                          </span>
                        )}
                      </label>
                      {/* Straight into the viewer with its crop panel open:
                          the document is worth seeing large while it is being
                          straightened, and the card keeps its place in the
                          grid instead of stretching across the row. */}
                      <button
                        type="button"
                        onClick={() => openViewer(a.id, true)}
                        style={smallBtn}
                      >
                        Crop / Adjust
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Appended pages — each holds one to four documents */}
              <div style={{ marginTop: "14px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  <span>
                    Appended A4 pages{" "}
                    <span style={{ fontWeight: 400, color: "#666" }}>
                      ({attachmentPages.length} page
                      {attachmentPages.length === 1 ? "" : "s"} after the form)
                    </span>
                  </span>
                  <button type="button" onClick={onAddPage} style={smallBtn}>
                    + Add page
                  </button>
                </div>

                {attachmentPages.length === 0 && (
                  <div style={{ fontSize: "12.5px", color: "#666" }}>
                    No extra pages yet. Add one to put two or three documents on a
                    single A4 sheet.
                  </div>
                )}

                {attachmentPages.map((p, pageIdx) => (
                  <div
                    key={p.id}
                    style={{ border: "1px solid #ddd", padding: "8px", marginBottom: "8px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "8px",
                        fontSize: "12.5px",
                      }}
                    >
                      <b>Page {pageIdx + 1}</b>
                      <select
                        value={p.layout}
                        onChange={(e) =>
                          onSetPageLayout(p.id, e.target.value as AttachmentLayout)
                        }
                        style={{ ...inputStyle, width: "auto" }}
                      >
                        {ATTACHMENT_LAYOUTS.map((l) => (
                          <option key={l.key} value={l.key}>
                            {l.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => onRemovePage(p.id)}
                        style={{ ...smallBtn, marginLeft: "auto" }}
                      >
                        Remove page
                      </button>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: "8px",
                      }}
                    >
                      {Array.from({ length: slotsForLayout(p.layout) }).map((_, slot) => (
                        <div
                          key={slot}
                          style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                        >
                          <span style={{ fontSize: "11px", color: "#666" }}>
                            Slot {slot + 1}
                          </span>
                          <select
                            value={p.items[slot] || ""}
                            onChange={(e) => onSetPageSlot(p.id, slot, e.target.value)}
                            style={inputStyle}
                          >
                            <option value="">— empty —</option>
                            {optionsForSlot(p.id, slot).map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Section>

      {/*
        Full-size document viewer.

        A plain fixed overlay rather than a dialog component: step 1 is a page,
        not a modal. Two columns — the document with its bar and its Prev / Next
        on the left, the Crop / Adjust panel on the right when it is open. It
        renders the edited copy when there is one, so what the operator inspects
        is what the PDF will carry.
      */}
      {viewed && (
        <div
          role="dialog"
          aria-label={viewed.label}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(0,0,0,0.82)",
            display: "flex",
            alignItems: "stretch",
          }}
        >
          {/* ------------------------------------------------ the document --- */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* One bar, one row, one height for everything in it: the name and
                position on the left, the actions on the right. */}
            <div
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                padding: "10px 14px",
                background: "#111",
                borderBottom: "1px solid #333",
                color: "#fff",
                fontSize: "13px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {viewed.label}
                </span>
                {/* Where the operator is in the pile. Without it Prev / Next
                    are two buttons with no idea how far they go. */}
                <span style={{ color: "#aaa", fontSize: "12px", whiteSpace: "nowrap" }}>
                  {viewerIndex + 1} of {attachments.length}
                </span>
                {viewed.edited && (
                  <span style={{ color: "#aaa", fontSize: "12px", whiteSpace: "nowrap" }}>
                    (adjusted for print)
                  </span>
                )}
              </span>

              {/* The two decisions the operator makes about a document — does
                  it go in, and does it need straightening — are made while
                  LOOKING at it full size, so both live here. */}
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ ...viewerBtn, fontWeight: viewed.selected ? 700 : 400 }}>
                  <input
                    type="checkbox"
                    checked={viewed.selected}
                    onChange={() => onToggleAttachment(viewed.id)}
                  />
                  Attach
                </label>
                <button
                  type="button"
                  onClick={() => setCropOpen((open) => !open)}
                  style={{
                    ...viewerBtn,
                    // Pressed while its panel is open: the panel is the button's
                    // own doing, so the button has to look responsible for it.
                    background: cropOpen ? "#fff" : "transparent",
                    color: cropOpen ? "#000" : "#fff",
                    borderColor: cropOpen ? "#fff" : "#6b6b6b",
                  }}
                >
                  Crop / Adjust
                </button>
                <a
                  href={viewed.edited || viewed.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...viewerBtn, textDecoration: "none" }}
                >
                  Open in new tab
                </a>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close (Esc)"
                  onClick={closeViewer}
                  style={{ ...viewerBtn, padding: "0 9px", fontSize: "14px" }}
                >
                  &#10006;
                </button>
              </span>
            </div>

            {/*
              What ticking Attach actually did, and how to change it.

              Attach on its own puts the document on a sheet of its own at the
              end of the PDF — fine for a full-page scan, wrong for the two
              small cards that belong side by side on one sheet. That was only
              fixable from the page builder at the foot of the form, which the
              viewer covers, so the answer and the controls live here too: which
              sheet this document is on, what else is on it, and how many
              documents that sheet holds.
            */}
            {viewed.selected && (
              <div
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                  padding: "8px 14px",
                  background: "#1c1c1c",
                  borderBottom: "1px solid #333",
                  color: "#ddd",
                  fontSize: "12px",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>Prints on</span>
                  <select
                    value={
                      pageIndexOf(viewed.id) >= 0
                        ? attachmentPages[pageIndexOf(viewed.id)].id
                        : "new"
                    }
                    onChange={(e) => onMoveAttachmentToPage(viewed.id, e.target.value)}
                    style={{ fontSize: "12px", padding: "3px 4px" }}
                  >
                    {attachmentPages.map((p, i) => (
                      <option key={p.id} value={p.id}>
                        Sheet {i + 1}
                      </option>
                    ))}
                    <option value="new">A sheet of its own</option>
                  </select>
                </span>

                {pageIndexOf(viewed.id) >= 0 && (
                  <>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>holding</span>
                      <select
                        value={attachmentPages[pageIndexOf(viewed.id)].layout}
                        onChange={(e) =>
                          onSetPageLayout(
                            attachmentPages[pageIndexOf(viewed.id)].id,
                            e.target.value as AttachmentLayout
                          )
                        }
                        style={{ fontSize: "12px", padding: "3px 4px" }}
                      >
                        {ATTACHMENT_LAYOUTS.map((l) => (
                          <option key={l.key} value={l.key}>
                            {l.label}
                          </option>
                        ))}
                      </select>
                    </span>
                    {/* Everything else already on that sheet, so moving a
                        document onto it is a decision made with the answer in
                        view rather than from memory. */}
                    <span style={{ color: "#9a9a9a", minWidth: 0 }}>
                      {(() => {
                        const sheet = attachmentPages[pageIndexOf(viewed.id)];
                        const others = sheet.items
                          .filter((i) => i && i !== viewed.id)
                          .map((i) => attachments.find((a) => a.id === i)?.label)
                          .filter(Boolean);
                        const free =
                          slotsForLayout(sheet.layout) - sheet.items.filter(Boolean).length;
                        return others.length
                          ? `with ${others.join(", ")}${free > 0 ? ` · ${free} space${free > 1 ? "s" : ""} left` : ""}`
                          : free > 0
                          ? `${free} more can join this sheet`
                          : "sheet full";
                      })()}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* The document, with a step button on each side of it. The click
                that closes lands on the padding around them — the image itself
                must not close the viewer, because the operator zooms by leaning
                in and a click that dismisses what they are reading is a trap. */}
            <div
              onClick={(e) => {
                if (e.target === e.currentTarget && !cropOpen) closeViewer();
              }}
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "14px",
                cursor: cropOpen ? "default" : "zoom-out",
              }}
            >
              <button
                type="button"
                aria-label="Previous document"
                title="Previous document (\u2190)"
                disabled={!hasPrev}
                onClick={() => stepViewer(-1)}
                style={viewerStepBtn(hasPrev)}
              >
                &#8249;
              </button>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={viewed.edited || viewed.url}
                  alt={viewed.label}
                  crossOrigin="anonymous"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    background: "#fff",
                    cursor: "default",
                  }}
                />
              </div>
              <button
                type="button"
                aria-label="Next document"
                title="Next document (\u2192)"
                disabled={!hasNext}
                onClick={() => stepViewer(1)}
                style={viewerStepBtn(hasNext)}
              >
                &#8250;
              </button>
            </div>

            <div
              style={{
                flex: "0 0 auto",
                textAlign: "center",
                color: "#9a9a9a",
                fontSize: "11.5px",
                padding: "0 14px 12px",
              }}
            >
              {cropOpen
                ? "Crop & Use moves to the NEXT document with the panel still open, so the whole pile is cropped in one pass \u00b7 Prev / Next step without cropping \u00b7 Esc closes the panel"
                : "Esc closes \u00b7 \u2190 \u2192 moves to the previous / next document \u00b7 ticking Attach is what puts it in the PDF"}
            </div>
          </div>

          {/* ------------------------------------------- crop / adjust panel --- */}
          {cropOpen && (
            <aside
              style={{
                flex: "0 0 auto",
                width: "clamp(360px, 38vw, 720px)",
                background: "#fff",
                borderLeft: "1px solid #000",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "10px 12px",
                  borderBottom: "1px solid #ddd",
                  background: "#fafafa",
                }}
              >
                <span
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Crop / Adjust
                </span>
                {/* The same Prev / Next the viewer carries, repeated here.
                    Once the panel stays open across a step, the operator's
                    hand is on THIS side of the screen — reaching back over the
                    document to the viewer's arrows to skip a scan that needs
                    no crop is the one thing that would still break the pass.
                    The counter comes with them, because "next" means nothing
                    without knowing how many are left. */}
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginLeft: "auto",
                  }}
                >
                  <button
                    type="button"
                    title="Previous document \u2014 nothing is cropped"
                    disabled={!hasPrev}
                    onClick={() => stepViewer(-1)}
                    style={stepBtn(hasPrev)}
                  >
                    &#8249; Back
                  </button>
                  <span
                    style={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}
                  >
                    {viewerIndex + 1} of {attachments.length}
                  </span>
                  <button
                    type="button"
                    title="Next document \u2014 nothing is cropped"
                    disabled={!hasNext}
                    onClick={() => stepViewer(1)}
                    style={stepBtn(hasNext)}
                  >
                    Next &#8250;
                  </button>
                </span>
                <button
                  type="button"
                  onClick={() => setCropOpen(false)}
                  title="Close the panel (Esc) \u2014 nothing is changed until Crop & Use"
                  style={smallBtn}
                >
                  Close
                </button>
              </div>
              {/* The panel scrolls on its own: the workspace plus the sliders is
                  taller than a laptop screen, and the overlay behind it must not
                  scroll the form underneath. */}
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px" }}>
                {/* Keyed on the document, so stepping to another one never
                    hands its crop box to the next image. */}
                <AttachmentCropPanel
                  key={viewed.id}
                  attachment={viewed}
                  onEdited={onAttachmentEdited}
                  onCommitted={cropCommitted}
                  onCancel={() => setCropOpen(false)}
                />
              </div>
            </aside>
          )}
        </div>
      )}

    </div>
  );
};

export default PanFormStepOne;
